/**
 * Poolwerx Brand Colors
 * Based on official Poolwerx website branding
 */

export const poolwerxColors = {
  // Primary Teal (official Poolwerx brand color)
  teal: '#007f98',
  tealHover: '#006680',
  tealLight: '#e0f4f8',

  // Accent shades for modern SaaS-style surfaces (exception reports etc.).
  // Live alongside the base brand teal/red so designers can pick a shade
  // for hover/active/badge without jumping to a different file. Do NOT
  // repoint `teal`/`red` to these - existing screens keep their look.
  tealAccent: '#008A93',
  redAccent: '#EF2E54',
  aqua: '#00B2CA',

  // Primary Red/Pink for actions and CTAs
  red: '#e91e63', // Material Design pink that matches Poolwerx red
  redHover: '#c2185b', // Darker shade for hover states
  redLight: '#f8bbd9', // Light shade for backgrounds
  
  // Supporting colors
  blue: '#2196f3',
  blueLight: '#e3f2fd',
  
  // Status colors
  success: '#4caf50',
  warning: '#ff9800',
  error: '#f44336',

  // Modal-header variant icon colours. Darker, accessible "icon-on-light-tint"
  // shades (the header badge tints each at 12% alpha) - distinct from the bright
  // success/warning/error set above, which is tuned for chips/text. Teal-family
  // variants (info/edit/sync/announcement) reuse `darkCyan`/`teal`; the rest are
  // defined here. See `components/shared/dialogs/modalVariants.tsx`.
  variantAmber: '#B54708', // alert
  variantGreen: '#2E7D32', // confirmation
  variantRed: '#C62828', // destructive
  variantSlate: '#41748A', // muted blue-teal for 'question'

  // Inspector alert stack tokens
  charcoal: '#333333',
  // Floating-toast palette (Google-style dark snackbar). Self-contained set so
  // the dark surface reads as one system; not reused by light page chrome.
  toastSurface: '#303134',          // charcoal card background
  toastText: '#F1F3F4',             // message text (light grey/white)
  toastAction: '#8AB4F8',           // text-only action link (softer blue)
  toastActionHover: '#AECBFA',      // action link hover
  toastClose: '#BDC1C6',            // muted close icon
  // Softer severity icon colours - the ONLY severity cue on the dark card.
  toastSuccess: '#81C995',
  toastError: '#F28B82',
  toastWarning: '#FDD663',
  toastInfo: '#8AB4F8',
  marketingRed: '#EC1C5A',
  darkCyan: '#007A87',
  errorSoft: 'rgba(236, 28, 90, 0.08)',
  warningSoft: 'rgba(255, 152, 0, 0.10)',
  // Info tint kept lighter than warning so a stale-data nudge stays the
  // primary attention item on screens that show both rows.
  infoSoft: 'rgba(0, 127, 152, 0.05)',

  // Stock level colors
  stockPositive: '#2e7d32', // Soft green for positive stock
  stockNegative: '#d32f2f', // Soft red for negative stock
  
  // Margin level colors
  marginNegative: '#f44336', // Red for negative/zero margins (cost >= retail)
  marginLow: '#ff9800',      // Orange for low margins (0-20%)
  marginGood: '#4caf50',     // Green for good margins (>20%) - optional for explicit styling

  // ── Semantic neutral layer ──────────────────────────────────────────────
  // Named text/border/surface neutrals so component styling resolves greys
  // through one set of names instead of scattered hex. Values are defined here
  // independently on purpose - they are NOT aliased to like-valued constants
  // such as SORT_ICON_COLOR / DISABLED_FG, which serve unrelated jobs and could
  // drift. Same hex today, separate concept.
  text: {
    primary: '#333333', // body / heading text
    secondary: '#5F6B70', // supporting text, subtitles
    tertiary: '#9CA3AF', // captions, timestamps, meta
  },
  border: {
    secondary: '#D8E0E3', // button outlines, stronger hairlines
    tertiary: '#E7ECEE', // row / zone hairlines (0.5px dividers)
  },
  surface: {
    primary: '#FFFFFF', // cards / rail-icon backing
    secondary: '#F1F5F6', // neutral chips (type tag), inactive count badge
  },

  // Neutral colors
  grey: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  }
}

// CSS custom properties for easy usage
export const poolwerxCssVars = {
  '--poolwerx-teal': poolwerxColors.teal,
  '--poolwerx-red': poolwerxColors.red,
  '--poolwerx-red-hover': poolwerxColors.redHover,
  '--poolwerx-red-light': poolwerxColors.redLight,
  '--poolwerx-blue': poolwerxColors.blue,
  '--poolwerx-blue-light': poolwerxColors.blueLight,
}